<script setup>
  const { t } = useI18n()
  const router = useRouter()
  const localePath = useLocalePath()

  const { isModalOpen, query, loading, totalResults, hasResults, resultTypes, closeModal, clearSearch } =
    useGlobalSearch()

  // #region Local state

  const inputRef = ref(null)

  // #endregion

  // #region Events

  // When the modal becomes visible, focus the input.
  watch(isModalOpen, (open) => {
    if (open) {
      nextTick(() => {
        inputRef.value?.$el?.focus?.() || inputRef.value?.focus?.()
      })
    } else {
      clearSearch()
    }
  })

  // #endregion

  // #region Methods

  /**
   * Navigate to full search results page.
   */
  function seeAllResults() {
    if (!query.value) return

    router.push(localePath({ name: 'search', query: { q: query.value } }))

    closeModal()
  }

  /**
   * Called when a result item is clicked — close the modal.
   */
  function handleItemNavigate() {
    closeModal()
  }

  /**
   * Clear input.
   */
  function handleClear() {
    clearSearch()

    nextTick(() => {
      inputRef.value?.$el?.focus?.() || inputRef.value?.focus?.()
    })
  }

  // #endregion
</script>

<template>
  <Dialog
    v-model:visible="isModalOpen"
    modal
    :header="null"
    :show-header="false"
    :dismissable-mask="true"
    :draggable="false"
    class="search-modal"
    :pt="{
      root: 'w-full max-w-[640px] mx-auto !mt-[10vh] sm:!mt-[15vh] !mb-0 rounded-xl shadow-2xl',
      mask: '!bg-slate-900/50 backdrop-blur-sm !items-start',
      content: '!p-0 !rounded-xl overflow-hidden'
    }"
    @hide="clearSearch"
  >
    <!-- Search input area -->
    <div class="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white">
      <font-awesome class="text-slate-400 text-lg flex-shrink-0" :icon="['fas', 'magnifying-glass']" />
      <input
        ref="inputRef"
        :value="query"
        type="text"
        :placeholder="t('globalSearch.placeholder')"
        class="flex-1 text-base text-slate-700 placeholder-slate-400 outline-none bg-transparent"
        @input="query = $event.target.value"
        @keydown.enter="seeAllResults"
        @keydown.escape="closeModal"
      />
      <div v-if="loading" class="flex-shrink-0">
        <font-awesome class="text-sky-500 animate-spin" :icon="['fas', 'spinner']" />
      </div>
      <button
        v-else-if="query"
        class="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
        @click="handleClear"
      >
        <font-awesome :icon="['fas', 'xmark']" />
      </button>
    </div>

    <!-- Results area -->
    <div class="max-h-[60vh] overflow-y-auto">
      <!-- Minimum characters hint -->
      <div v-if="query && query.length < 2" class="px-4 py-8 text-center text-sm text-slate-400">
        {{ t('globalSearch.minChars') }}
      </div>

      <!-- Loading skeleton -->
      <div v-else-if="loading && !hasResults" class="px-4 py-6 space-y-3">
        <div v-for="i in 3" :key="i" class="flex items-center gap-3 animate-pulse">
          <div class="w-10 h-10 bg-slate-200 rounded-lg" />
          <div class="flex-1 space-y-2">
            <div class="h-3 bg-slate-200 rounded w-3/4" />
            <div class="h-2 bg-slate-100 rounded w-1/2" />
          </div>
        </div>
      </div>

      <!-- No results -->
      <div v-else-if="query && query.length >= 2 && !loading && !hasResults" class="px-4 py-8 text-center">
        <font-awesome class="text-3xl text-slate-200 mb-3" :icon="['fas', 'search']" />
        <p class="text-sm text-slate-500">
          {{ t('globalSearch.noResults', { query: query }) }}
        </p>
      </div>

      <!-- Grouped results -->
      <template v-else>
        <div v-for="group in resultTypes" :key="group.type" class="border-b border-slate-100 last:border-b-0">
          <!-- Group header -->
          <div class="flex items-center justify-between px-4 pt-3 pb-1">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {{ t(`globalSearch.types.${group.type}`) }}
              </span>
              <span class="text-xs text-slate-400 font-normal"> ({{ group.total }}) </span>
            </div>
          </div>

          <!-- Items -->
          <div class="pb-2">
            <SearchResultItem
              v-for="item in group.items"
              :key="item.documentId || item.id"
              :item="item"
              :query="query"
              @navigate="handleItemNavigate"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- Footer — See all results -->
    <div v-if="hasResults" class="border-t border-slate-200 bg-slate-50 px-4 py-3">
      <button
        class="w-full flex items-center justify-center gap-2 text-sm font-medium text-sky-600 hover:text-sky-800 transition-colors cursor-pointer"
        @click="seeAllResults"
      >
        <span>{{ t('globalSearch.seeAll') }}</span>
        <span class="text-xs text-slate-400">({{ totalResults }})</span>
        <font-awesome class="text-xs" :icon="['fas', 'arrow-right']" />
      </button>
    </div>

    <!-- Empty state — no input yet -->
    <div v-if="!query" class="px-4 py-8 text-center text-sm text-slate-400">
      {{ t('globalSearch.placeholder') }}
    </div>
  </Dialog>
</template>

<style>
  /* Ensure the search modal sits above the header */
  .search-modal {
    z-index: 1200;
  }
</style>
